import React from 'react';
import { 
  Shield, Package, CheckCircle, AlertTriangle, 
  Clock, CreditCard, History, ArrowRight
} from 'lucide-react';
import Modal from '../../components/common/Modal';
import EscrowStatusBadge from '../../components/chat/EscrowStatusBadge';

const EscrowModal = ({
  isOpen,
  onClose,
  activeEscrow,
  escrowHistory,
  isCurrentUserBuyer,
  isCurrentUserSeller,
  sellerName,
  sellerStoreName,
  buyerName,
  formatPrice,
  renderEscrowActions,
  navigate,
  conversationId
}) => {
  // Get display names for modal
  const getBuyerDisplayName = () => {
    if (isCurrentUserBuyer) return 'You';
    return activeEscrow?.buyer_name || buyerName || `Buyer ${activeEscrow?.buyer_id}`;
  };

  const getSellerDisplayName = () => {
    if (isCurrentUserSeller) return 'You';
    return sellerStoreName || sellerName || `Seller ${activeEscrow?.seller_id}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isCurrentUserBuyer ? "Secure Payment - Escrow" : "Escrow Transaction"}
      size="md"
      showCloseButton={true}
      showFooter={false}
    >
      <div className="space-y-4">
        {activeEscrow ? (
          <>
            {/* Escrow Status Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className={`w-5 h-5 ${
                  activeEscrow.status === 'released' ? 'text-green-600' :
                  activeEscrow.status === 'disputed' ? 'text-red-600' :
                  activeEscrow.status === 'held' ? 'text-blue-600' :
                  'text-yellow-600'
                }`} />
                <span className="font-medium text-gray-900">
                  {isCurrentUserBuyer ? 'Your Payment Protection' : 'Escrow Transaction'}
                </span>
              </div>
              <EscrowStatusBadge status={activeEscrow.status} />
            </div>
            
            {/* Transaction Parties */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="mb-3">
                <div className="flex items-center justify-between">
                  {/* Buyer Info */}
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold">
                      {getBuyerDisplayName().charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Buyer</p>
                      <p className="text-sm font-medium text-gray-900">
                        {getBuyerDisplayName()}
                      </p>
                    </div>
                  </div>
                  
                  <ArrowRight className="w-4 h-4 text-gray-400 mx-2" />
                  
                  {/* Seller Info */}
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center text-white font-bold">
                      {getSellerDisplayName().charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Seller</p>
                      <p className="text-sm font-medium text-gray-900">
                        {getSellerDisplayName()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Amount and Dates */}
              <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-200">
                <div>
                  <p className="text-sm text-gray-600">Amount in escrow</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatPrice(activeEscrow.amount)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Created</p>
                  <p className="text-sm text-gray-900">
                    {new Date(activeEscrow.created_at).toLocaleDateString('en-NG', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Role-specific instructions */}
            {isCurrentUserBuyer && activeEscrow.status === 'held' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Package className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-blue-800 mb-1">Next Steps for You (Buyer)</p>
                    <p className="text-sm text-blue-700">
                      Funds are secured. Once you receive the item, confirm receipt to release payment to the seller.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {isCurrentUserSeller && activeEscrow.status === 'held' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Package className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-green-800 mb-1">Next Steps for You (Seller)</p>
                    <p className="text-sm text-green-700">
                      Funds are secured in escrow. You can now ship the item. The buyer will release payment upon receipt.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Actions */}
            {renderEscrowActions()}
            
            {/* Transaction Details */}
            {/* <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between text-sm">
                <div>
                  <p className="text-gray-600">Transaction ID</p>
                  <p className="font-mono text-gray-900">{activeEscrow.escrow_id}</p>
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(activeEscrow.escrow_id);
                    alert('Transaction ID copied to clipboard!');
                  }}
                  className="text-orange-600 hover:text-orange-700 text-xs font-medium"
                >
                  Copy ID
                </button>
              </div>
            </div> */}
            
            {/* History Button */}
            {/* {escrowHistory.length > 0 && (
              <button
                onClick={() => navigate(`/chats/${conversationId}/escrow-history`)}
                className="w-full border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                <History className="w-4 h-4" />
                View Escrow History
              </button>
            )} */}
          </>
        ) : (
          <div className="text-center py-6">
            <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h4 className="font-semibold text-gray-900 mb-2">
              {isCurrentUserBuyer ? 'No Active Escrow' : 'No Escrow Transaction'}
            </h4>
            <p className="text-gray-600 mb-4">
              {isCurrentUserBuyer 
                ? 'Secure your payment by depositing funds into escrow' 
                : 'Escrow will be activated when buyer makes a payment'}
            </p>
            {renderEscrowActions()}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EscrowModal;